import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import Footer from '../../components/Footer';
import FeatureIcon, { ICON_TONES } from '../../components/FeatureIcon';
import ScreenshotDetail from '../../components/ScreenshotDetail';
import SegmentedTabs from '../../components/SegmentedTabs';
import { premiumGold } from '../../components/canonicalAccents';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { selectedCaptureSrc } from '../../lib/selectedCaptures';
import './Pro.css';

const AnimatedSection: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 1.2s ease-out, transform 1s ease-out'
            }
      }
    >
      {children}
    </div>
  );
};

const SNAP = 'snapshots/20261005T052529Z';
const SNAP_20261006 = 'snapshots/20261006T002614Z';
const PRO_BASE = `/assets/screenshots/ios-delivery/${SNAP}/Pro`;
const DELIVERY_BASE = '/assets/screenshots/ios-delivery';

const APP_VARIANTS = [
  {
    label: 'Nomad',
    src: `${PRO_BASE}/iOS - Pro - App Theme - Light.png`,
    crop: { x: 0, y: 0, width: 1320, height: 2280 },
    alt: 'RidePulse profile with the Nomad app theme applied, including the App Themes selection; account controls are below the shown excerpt',
    caption: 'Nomad theme.',
    fullImageLabel: 'View full screen with the Nomad theme',
  },
  {
    label: 'After Dark',
    src: `${PRO_BASE}/iOS - Pro - App Theme - Dark.png`,
    crop: { x: 0, y: 0, width: 1320, height: 1880 },
    alt: 'RidePulse profile with the After Dark app theme applied, including the App Themes selection; account controls are below the shown excerpt',
    caption: 'After Dark theme.',
    fullImageLabel: 'View full screen with the After Dark theme',
  },
];

const Pro: React.FC = () => {
  const { theme, dsColors, colors } = useTheme();
  const [appVariant, setAppVariant] = useState(0);

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBorder = theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border;
  const bandBg = theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)';
  const gold = premiumGold(theme);
  const hairline = theme === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.14)';

  const eyebrow = 'text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3';
  const sectionTitle = 'text-3xl sm:text-4xl font-bold mb-4';
  const sectionLead = 'text-lg leading-relaxed';

  const link = (to: string, label: string) => (
    <Link
      to={to}
      className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
      style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
    >
      {label} →
    </Link>
  );

  return (
    <div className="min-h-screen pro-page" style={{ backgroundColor: colors.background }}>
      {/* 1 · Hero */}
      <section className="pt-20 pb-12 md:pt-28 md:pb-16" style={{ backgroundColor: colors.surface }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl grid gap-10 lg:grid-cols-[1fr_300px] lg:gap-14 items-center">
          <div className="max-w-2xl">
            <p className={`${eyebrow} inline-flex items-center gap-2`} style={{ color: gold }}>
              <FeatureIcon name="crown" size={14} />RidePulse Pro
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-7 leading-[1.05]" style={{ color: heroPrimary, textWrap: 'balance' }}>
              Make more of RidePulse.
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
              Make the app your own, bring more of your motorcycles into your Garage, and get more from the events you host.
            </p>
          </div>
          <div className="w-[240px] sm:w-[260px] lg:w-full mx-auto">
            <ScreenshotDetail
              src={selectedCaptureSrc('proOverview', theme)}
              crop={{ x: 0, y: 0, width: 1320, height: 2868 }}
              alt="The RidePulse Pro overview in the app showing an active membership"
              caption="Pro features in RidePulse."
              fullImageLabel="View the full Pro overview screen"
              priority
            />
          </div>
        </div>
      </section>

      {/* 2 · Theme studio: centered control over one bounded stage */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="max-w-xl mx-auto text-center mb-10">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Make RidePulse look like yours.</h2>
              <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Choose a different look for the app you use on every ride.
              </p>
            </div>
            <div
              className="pro-studio mx-auto"
              style={{ '--st-accent': dsColors.primary, '--st-hairline': hairline, '--st-text': heroPrimary } as React.CSSProperties}
            >
              <SegmentedTabs
                label="App theme"
                options={APP_VARIANTS.map((v) => v.label)}
                selected={appVariant}
                onSelect={setAppVariant}
              >
                <div className="pro-studio-stage mx-auto">
                  <ScreenshotDetail
                    src={APP_VARIANTS[appVariant].src}
                    crop={APP_VARIANTS[appVariant].crop}
                    alt={APP_VARIANTS[appVariant].alt}
                    caption={APP_VARIANTS[appVariant].caption}
                    fullImageLabel={APP_VARIANTS[appVariant].fullImageLabel}
                    stageAspectRatio="1320 / 2280"
                    priority
                  />
                </div>
              </SegmentedTabs>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 3 · Event styling: copy above a full-bleed card excerpt */}
      <section className="py-12 md:py-16" style={{ backgroundColor: bandBg }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="max-w-2xl mx-auto text-center mb-10">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Give your event its own look.</h2>
              <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Choose a card theme that fits the event you’re putting together.
              </p>
            </div>
            <div className="pro-card mx-auto">
              <ScreenshotDetail
                src={`${PRO_BASE}/iOS - Pro - Event Card Theme - ${theme === 'dark' ? 'Dark' : 'Light'}.png`}
                crop={{ x: 48, y: 720, width: 1224, height: 500 }}
                alt="The Racing Red event card theme applied to a Desert Dawn event in RidePulse"
                caption="Racing Red event card."
                fullImageLabel="View full-size Racing Red event card"
              />
            </div>
            <div className="pro-media-strip mt-10 max-w-2xl mx-auto">
              <span className="inline-flex flex-shrink-0 mt-0.5" style={{ color: heroSecondary }} aria-hidden="true">
                <FeatureIcon name="image" size={20} />
              </span>
              <div>
                <h3 className="text-base font-semibold mb-1" style={{ color: heroPrimary }}>Add flyers and videos to your event.</h3>
                <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Help riders get a feel for the event before they join.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 4 · Expanded Garage: copy above a bounded collection excerpt */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="max-w-2xl mx-auto text-center mb-10">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Room for more of your motorcycles.</h2>
              <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Pro expands your Garage, so more of the bikes you ride can be ready for your next event.
              </p>
              <p className="text-sm mt-4" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Capacity is the Pro benefit. Bike photos, specifications and everyday Garage tools are available without Pro.
              </p>
            </div>
            <div className="pro-garage-detail mx-auto">
              <ScreenshotDetail
                src={`${DELIVERY_BASE}/${SNAP_20261006}/Pro/iOS - Pro - Five Bike Garage More - ${theme === 'dark' ? 'Dark' : 'Light'}.png`}
                crop={{ x: 48, y: 514, width: 1224, height: 1134 }}
                alt="RidePulse Garage showing Red Rock, a 2023 Aprilia RS 660, with its matching motorcycle photo"
                caption="Your Garage."
                fullImageLabel="View the full Garage screen"
              />
              <div className="mt-3">
                <ScreenshotDetail
                  src={`${DELIVERY_BASE}/${SNAP_20261006}/Pro/iOS - Pro - Five Bike Garage - ${theme === 'dark' ? 'Dark' : 'Light'}.png`}
                  crop={{ x: 48, y: 625, width: 1224, height: 160 }}
                  alt="RidePulse Pro Garage capacity indicator showing 5 of 5 slots used"
                  caption="Pro Garage capacity."
                  fullImageLabel="View the full Garage screen"
                />
              </div>
            </div>
            <div className="mt-6 text-center">
              {link('/garage', 'Explore the Garage')}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 5 · GroupPulse: quiet wide spotlight, icon and label only */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="pro-gp-spotlight rounded-2xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
              <div className="pro-gp-inner p-6 sm:p-10">
                <div className="pro-gp-icon" aria-hidden="true">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl" style={{ border: `1px solid ${cardBorder}`, color: heroSecondary }}>
                    <FeatureIcon name="map" size={30} />
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full" style={{ border: `1px solid ${gold}`, color: gold }}>
                    <FeatureIcon name="crown" size={12} />Pro
                  </span>
                </div>
                <div className="max-w-2xl">
                  <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>One host enables it. The group can take part.</h2>
                  <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                    A Pro host can enable GroupPulse for the event, giving participating riders a shared view of the ride.
                  </p>
                  <p className="text-sm mt-5" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                    GroupPulse is a Pro feature for the host. Every rider then decides for themselves whether, and how, to share.
                  </p>
                  <div className="mt-6">
                    {link('/grouppulse', 'How GroupPulse works')}
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 6 · Hosting additions: illustrated capability band */}
      <section className="py-12 md:py-16" style={{ backgroundColor: bandBg }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="max-w-xl mb-10">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>More from the events you host.</h2>
            </div>
            <div className="pro-cap-wrap">
            <div className="pro-cap" style={{ '--pro-hairline': hairline } as React.CSSProperties}>
              {([
                {
                  area: 'events',
                  iconName: 'calendar' as const,
                  tone: dsColors[ICON_TONES.attention],
                  title: 'More active events',
                  desc: 'Create more active events.',
                },
                {
                  area: 'waypoints',
                  iconName: 'route' as const,
                  tone: dsColors[ICON_TONES.community],
                  title: 'Waypoints',
                  desc: 'Add stops to the plan, from fuel breaks to photo opportunities.',
                },
                {
                  area: 'criteria',
                  iconName: 'shield' as const,
                  tone: dsColors[ICON_TONES.neutral],
                  title: 'More ways to shape your event',
                  desc: 'Add motorcycle brand and color rules to the free engine-size and rider-experience requirements.',
                },
                {
                  area: 'sponsors',
                  iconName: 'link' as const,
                  tone: dsColors[ICON_TONES.attention],
                  title: 'Expanded sponsor capacity',
                  desc: 'Include more sponsors while basic sponsor presentation remains available without Pro.',
                },
              ]).map((b) => (
                <div key={b.title} className={`pro-cap-item pro-cap-${b.area}`}>
                  <span className="inline-flex flex-shrink-0" style={{ color: b.tone }} aria-hidden="true">
                    <FeatureIcon name={b.iconName} size={22} />
                  </span>
                  <div>
                    <h3 className="font-semibold text-base mb-1" style={{ color: heroPrimary }}>{b.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            </div>
            <div className="mt-8">
              {link('/hosting', 'See the hosting tools')}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 7 · Analytics: its own prominent text-led surface */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
          <AnimatedSection>
            <div className="pro-analytics rounded-2xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
              <div className="pro-analytics-inner">
                <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl flex-shrink-0" style={{ border: `1px solid ${cardBorder}`, color: heroSecondary }} aria-hidden="true">
                  <FeatureIcon name="chart" size={30} />
                </span>
                <div className="max-w-2xl">
                  <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>See how your events turn out.</h2>
                  <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                    Look across your hosting history, participation and event performance.
                  </p>
                </div>
              </div>
              <div className="pro-analytics-evidence mx-auto">
                <ScreenshotDetail
                  src={selectedCaptureSrc('analytics', theme)}
                  crop={{ x: 48, y: 1700, width: 1224, height: 575 }}
                  alt="Aggregate event performance shown in RidePulse analytics"
                  caption="Aggregate performance."
                  fullImageLabel="View the full analytics screen"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 6 · Close: quiet pointer to the app and the guide */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-2xl">
          <AnimatedSection>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: heroPrimary, textWrap: 'balance' }}>
              Explore Pro in RidePulse.
            </h2>
            <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
              See current pricing and availability in the app.
            </p>
            <p className="text-sm mt-5" style={{ color: heroSecondary }}>
              Allowances and detailed qualifications are in the{' '}
              <Link
                to="/help#free-pro"
                className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
              >
                Free &amp; Pro guide
              </Link>
              .
            </p>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pro;
